// Graph view of Messaging Reports: each tab (Summary, Members, Channels, Groups, Appointment
// Reminders) as a few clean cards. It is a different layout over the same data the Table view
// uses; MessagingReports computes everything and passes it in. Lists show every entry and scroll
// inside their card instead of being cut to a top few or stretching the page.
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import { downloadCsv, categoryColor, REPORT_DIRECTION_COLORS } from '../helpers/ReportsHelpers';
import { OTHER_KEY } from '../helpers/MessagingReportsHelpers';
import {
  StackedBars, MultiLine, EmptyChart, ResponseByHourChart, OTHER_LABEL, OTHER_COLOR,
} from './MessagingReportsCharts';

export const DIRECTION_BARS = [
  { key: 'inbound', name: 'Inbound', color: REPORT_DIRECTION_COLORS.Inbound },
  { key: 'outbound', name: 'Outbound', color: REPORT_DIRECTION_COLORS.Outbound },
  { key: 'automated', name: 'Automated', color: REPORT_DIRECTION_COLORS.Automated },
];
const WEEKLY_AFTER_DAYS = 62; // longer ranges plot weekly totals so the line stays readable
const RESPONSE_COLOR = '#4A9B8E';
// Recharts tooltips: an explicit card so they stay readable over the donut's centre label.
const CHART_TOOLTIP = {
  wrapperStyle: { zIndex: 10 },
  contentStyle: {
    background: '#fff', border: '1px solid #e8ebf0', borderRadius: 8, boxShadow: '0 4px 12px rgba(16, 24, 40, 0.12)', fontSize: 15, lineHeight: 1.6, padding: '10px 14px',
  },
};

// Names arrive as "Name (deleted)" / "Name (deactivated)"; show the status as a quiet tag instead.
const STATUS_SUFFIX = /\s\((deleted|deactivated)\)$/;
export function splitStatus(name) {
  const match = STATUS_SUFFIX.exec(name || '');
  return match ? { label: name.slice(0, match.index), status: match[1] } : { label: name, status: null };
}

export function initialsOf(name) {
  const words = splitStatus(name).label.split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

const percentText = (value, total) => {
  if (!total) return '0%';
  const pct = (value / total) * 100;
  return pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
};

// Response times range from minutes to weeks, so pick the unit that reads naturally.
export function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} hr`;
  const days = hours / 24;
  return `${days < 10 ? days.toFixed(1) : Math.round(days)} days`;
}

const formatCount = (n) => Number(n || 0).toLocaleString();

// Sum a per-day series ({ day, date, <key>: count }) into weeks starting on the first day shown.
export function weeklyTotals(data, keys) {
  const weeks = [];
  data.forEach((point, i) => {
    if (i % 7 === 0) {
      weeks.push({ date: moment(point.day, 'YYYY-MM-DD').format('MM/DD'), day: point.day, ...Object.fromEntries(keys.map((k) => [k, 0])) });
    }
    const week = weeks[weeks.length - 1];
    keys.forEach((k) => { week[k] += point[k] || 0; });
  });
  return weeks;
}

// --- Building blocks ---

const CsvButton = ({ csv }) => (
  <button type="button" className="msg-overview__csv" onClick={() => downloadCsv(csv.columns, csv.rows, csv.filename)}>
    CSV
  </button>
);
CsvButton.propTypes = { csv: PropTypes.shape({ columns: PropTypes.array, rows: PropTypes.array, filename: PropTypes.string }).isRequired };

const Card = ({ title, subtitle, csv, badge, footer, className, children, onMouseMove, onMouseLeave }) => (
  <div className={`msg-overview__card${className ? ` ${className}` : ''}`} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
    <div className="msg-overview__card-header">
      <div>
        <h3 className="msg-overview__card-title">{title}</h3>
        {subtitle && <p className="msg-overview__card-subtitle">{subtitle}</p>}
      </div>
      <div className="msg-overview__card-actions">
        {badge && <span className="msg-overview__badge">{badge}</span>}
        {csv && <CsvButton csv={csv} />}
      </div>
    </div>
    <div className="msg-overview__card-body">{children}</div>
    {footer && <p className="msg-overview__card-footer">{footer}</p>}
  </div>
);
Card.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  csv: PropTypes.object,
  badge: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
  onMouseMove: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

const Avatar = ({ name }) => <span className="msg-overview__avatar" aria-hidden="true">{initialsOf(name)}</span>;
Avatar.propTypes = { name: PropTypes.string.isRequired };

const Name = ({ name }) => {
  const { label, status } = splitStatus(name);
  return (
    <span className="msg-overview__name" title={name}>
      {label}
      {status && <span className="msg-overview__status">{` · ${status}`}</span>}
    </span>
  );
};
Name.propTypes = { name: PropTypes.string.isRequired };

// Hover card for a bar row. Rendered into <body> with fixed positioning so the scrolling list
// (overflow: auto) can't clip it.
const HoverTip = ({ tip }) => {
  if (!tip) return null;
  const [title, ...lines] = tip.lines;
  return createPortal(
    <div className="msg-overview__hover-tip" style={{ left: tip.x + 14, top: tip.y + 14 }} role="tooltip">
      <div className="msg-overview__hover-tip-title">{title}</div>
      {lines.map((line) => <div key={line}>{line}</div>)}
    </div>,
    document.body,
  );
};
HoverTip.propTypes = { tip: PropTypes.shape({ lines: PropTypes.array, x: PropTypes.number, y: PropTypes.number }) };

// One row per entry: label, right-aligned value, and a thin bar (or a segmented bar when `segments`
// is given). Every entry is shown; the list scrolls inside its card when it is long. Hovering a row
// shows its underlying numbers (`row.tooltip`, first line is the title).
const BarList = ({ rows, withAvatars, empty }) => {
  const [tip, setTip] = useState(null);
  if (!rows.length) return <p className="msg-overview__empty">{empty}</p>;
  const track = (row) => (event) => {
    if (row.tooltip) setTip({ lines: row.tooltip, x: event.clientX, y: event.clientY });
  };
  return (
    <div className="msg-overview__scroll" onScroll={() => setTip(null)}>
      <ul className="msg-overview__bar-list">
        {rows.map((row) => (
          <li
            key={row.key}
            className={`msg-overview__bar-row${withAvatars ? ' msg-overview__bar-row--avatar' : ''}`}
            onMouseMove={track(row)}
            onMouseLeave={() => setTip(null)}
          >
            {withAvatars && <Avatar name={row.name} />}
            <div className="msg-overview__bar-content">
              <div className="msg-overview__bar-label">
                <Name name={row.name} />
                <span className="msg-overview__bar-value">{row.valueText}</span>
              </div>
              <div className="msg-overview__bar-track">
                {row.segments ? row.segments.map((seg) => (
                  <div
                    key={seg.key}
                    className="msg-overview__bar-fill msg-overview__bar-fill--segment"
                    style={{ width: `${seg.fraction * 100}%`, background: seg.color }}
                  />
                )) : (
                  <div className="msg-overview__bar-fill" style={{ width: `${Math.max(row.fraction * 100, row.fraction > 0 ? 1.5 : 0)}%`, background: row.color }} />
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <HoverTip tip={tip} />
    </div>
  );
};
BarList.propTypes = { rows: PropTypes.array.isRequired, withAvatars: PropTypes.bool, empty: PropTypes.string };
BarList.defaultProps = { empty: 'No data for this selection' };

// Share-of-total rows for every entry with a value, largest first.
const shareRows = (entries, colorAt, unit) => {
  const total = entries.reduce((sum, e) => sum + e.value, 0);
  return entries.filter((e) => e.value > 0).sort((a, b) => b.value - a.value).map((e, i) => ({
    key: e.key ?? e.name,
    name: e.name,
    valueText: percentText(e.value, total),
    fraction: total ? e.value / total : 0,
    color: colorAt(i),
    tooltip: [e.name, `${formatCount(e.value)} ${unit}`, `${percentText(e.value, total)} of ${formatCount(total)}`, ...(e.extra || [])],
  }));
};

// Count rows scaled to the largest entry, largest first.
const countRows = (entries, color, unit) => {
  const rows = entries.filter((e) => e.value > 0).sort((a, b) => b.value - a.value);
  const max = Math.max(0, ...rows.map((r) => r.value));
  return rows.map((e) => ({
    key: e.key ?? e.name,
    name: e.name,
    valueText: formatCount(e.value),
    fraction: max ? e.value / max : 0,
    color,
    tooltip: [e.name, `${formatCount(e.value)} ${unit}`],
  }));
};

// Response-time rows, slowest first, scaled to the slowest.
const responseRowsOf = (entries) => {
  const sorted = [...entries].sort((a, b) => b.avgResponseMinutes - a.avgResponseMinutes);
  const max = Math.max(0, ...sorted.map((r) => r.avgResponseMinutes));
  return sorted.map((r) => ({
    key: r.key,
    name: r.name,
    valueText: formatDuration(r.avgResponseMinutes),
    fraction: max ? r.avgResponseMinutes / max : 0,
    color: RESPONSE_COLOR,
    tooltip: [r.name, `Average response ${formatDuration(r.avgResponseMinutes)}`],
  }));
};

// Tiles with a big count each (open conversations per member or group). Scrolls when long.
const CountTiles = ({ rows, unit, color, empty }) => {
  if (!rows.length) return <p className="msg-overview__empty">{empty}</p>;
  const max = Math.max(0, ...rows.map((r) => r.value));
  return (
    <div className="msg-overview__scroll msg-overview__scroll--tiles">
      <ul className="msg-overview__open-grid">
        {rows.map((r) => {
          const { label, status } = splitStatus(r.name);
          return (
            <li key={r.key} className="msg-overview__open-member">
              <div className="msg-overview__open-head">
                <Avatar name={r.name} />
                <span className="msg-overview__open-name" title={r.name}>
                  <span className="msg-overview__name">{label}</span>
                  {status && <span className="msg-overview__open-status">{status}</span>}
                </span>
              </div>
              <div className="msg-overview__open-count">
                <span className="msg-overview__open-number">{formatCount(r.value)}</span>
                <span className="msg-overview__open-unit">{unit}</span>
              </div>
              <div className="msg-overview__bar-track">
                <div className="msg-overview__bar-fill" style={{ width: `${max ? (r.value / max) * 100 : 0}%`, background: color }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
CountTiles.propTypes = { rows: PropTypes.array.isRequired, unit: PropTypes.string.isRequired, color: PropTypes.string, empty: PropTypes.string.isRequired };
CountTiles.defaultProps = { color: REPORT_DIRECTION_COLORS.Inbound };

// Donut with the total in the centre and a percentage legend beside it.
const TotalDonut = ({ data, total, unit, colorFor }) => {
  const sum = data.reduce((acc, d) => acc + d.value, 0);
  if (!sum) return <EmptyChart height={200} />;
  return (
    <div className="msg-overview__donut-row">
      <div className="msg-overview__donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius="72%" outerRadius="100%" startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
              {data.map((d, i) => <Cell key={d.name} fill={colorFor(d.name, i)} />)}
            </Pie>
            <Tooltip formatter={(value) => [formatCount(value), unit]} {...CHART_TOOLTIP} />
          </PieChart>
        </ResponsiveContainer>
        <div className="msg-overview__donut-center">
          <span className="msg-overview__donut-total">{formatCount(total)}</span>
          <span className="msg-overview__donut-unit">{unit.toLowerCase()}</span>
        </div>
      </div>
      <ul className="msg-overview__legend">
        {data.map((d, i) => (
          <li key={d.name}>
            <span className="msg-overview__dot" style={{ background: colorFor(d.name, i) }} />
            <span className="msg-overview__legend-name">{d.name}</span>
            <span className="msg-overview__legend-value">{percentText(d.value, sum)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
TotalDonut.propTypes = { data: PropTypes.array.isRequired, total: PropTypes.number.isRequired, unit: PropTypes.string.isRequired, colorFor: PropTypes.func.isRequired };

// A headline number. `tooltip` lines (first is the title) show on hover, e.g. the breakdown behind it.
const StatCard = ({ title, subtitle, value, pill, tooltip }) => {
  const [tip, setTip] = useState(null);
  const hover = tooltip && tooltip.length > 1 ? {
    onMouseMove: (event) => setTip({ lines: tooltip, x: event.clientX, y: event.clientY }),
    onMouseLeave: () => setTip(null),
  } : {};
  return (
    <Card title={title} subtitle={subtitle} className="msg-overview__card--stat" {...hover}>
      <div className="msg-overview__stat">
        <span className="msg-overview__stat-value">{value}</span>
        {pill && <span className="msg-overview__pill">{pill}</span>}
      </div>
      <HoverTip tip={tip} />
    </Card>
  );
};
StatCard.propTypes = {
  title: PropTypes.node.isRequired, subtitle: PropTypes.node, value: PropTypes.node.isRequired, pill: PropTypes.node, tooltip: PropTypes.array,
};

// Lines per member/channel over the range; weekly totals for long ranges.
const OverTimeCard = ({ title, series: categorySeries, nameOf, dayCount, csvFilename }) => {
  const isWeekly = dayCount > WEEKLY_AFTER_DAYS;
  const timeline = useMemo(() => {
    const data = isWeekly ? weeklyTotals(categorySeries.data, categorySeries.keys) : categorySeries.data;
    const series = categorySeries.keys.map((k, i) => ({
      key: k,
      name: k === OTHER_KEY ? OTHER_LABEL : nameOf(Number(k)),
      color: k === OTHER_KEY ? OTHER_COLOR : categoryColor(i),
    }));
    return { data, series };
  }, [categorySeries, nameOf, isWeekly]);
  return (
    <Card
      title={title}
      subtitle={isWeekly ? 'Weekly totals' : 'Daily totals'}
      csv={{ columns: [{ key: 'day', header: isWeekly ? 'Week Of' : 'Date' }, ...timeline.series.map((s) => ({ key: s.key, header: s.name }))], rows: timeline.data, filename: csvFilename(title) }}
      className="msg-overview__card--wide"
      footer={isWeekly ? 'Messages / week' : 'Messages / day'}
    >
      <MultiLine data={timeline.data} series={timeline.series} height={280} />
    </Card>
  );
};
OverTimeCard.propTypes = {
  title: PropTypes.string.isRequired,
  series: PropTypes.shape({ data: PropTypes.array, keys: PropTypes.array }).isRequired,
  nameOf: PropTypes.func.isRequired,
  dayCount: PropTypes.number.isRequired,
  csvFilename: PropTypes.func.isRequired,
};

// One bar per day, so a day with no replies is a gap rather than a line drawn through it (a line
// implied a steady rise across days with no data). The range average is a dashed reference line.
const ResponsePerDayBars = ({ data, average, height }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
      <YAxis tickFormatter={(v) => formatDuration(v)} width={70} tick={{ fontSize: 12 }} />
      <Tooltip formatter={(value) => [formatDuration(value), 'Avg. response']} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} {...CHART_TOOLTIP} />
      {average !== null && average !== undefined && (
        <ReferenceLine
          y={average}
          stroke={OTHER_COLOR}
          strokeDasharray="4 4"
          label={{ value: `Range avg ${formatDuration(average)}`, position: 'insideTopRight', fill: '#6b7684', fontSize: 12 }}
        />
      )}
      <Bar dataKey="avgResponseMinutes" name="Avg. response" fill={REPORT_DIRECTION_COLORS.Inbound} radius={[4, 4, 0, 0]} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);
ResponsePerDayBars.propTypes = { data: PropTypes.array.isRequired, average: PropTypes.number, height: PropTypes.number };
ResponsePerDayBars.defaultProps = { height: 260 };

const shareSentence = (name, share) => {
  if (share >= 1) return `${name} accounts for all messages`;
  if (share >= 0.95) return `${name} accounts for almost all messages`;
  if (share >= 0.5) return `${name} accounts for most messages`;
  return `${name} is the largest channel type`;
};

// --- Tabs ---

export const OverviewSummary = ({
  totals, dayCount, directionPie, channelPie, channelTypePie, contacts, contactPie, hourSeries, weekdaySeries, daySeries, peak, avgResponseMinutes, csvFilename,
}) => {
  const avgPerDay = dayCount ? totals.total / dayCount : 0;
  const channelRows = useMemo(() => shareRows(channelPie, (i) => categoryColor(i), 'messages'), [channelPie]);
  const topType = useMemo(() => {
    const sum = channelTypePie.reduce((acc, t) => acc + t.value, 0);
    const top = [...channelTypePie].sort((a, b) => b.value - a.value)[0];
    return top && sum ? { name: top.name, share: top.value / sum } : null;
  }, [channelTypePie]);
  const contactSummary = useMemo(() => {
    const sources = [...contactPie].sort((a, b) => b.value - a.value);
    const parts = [];
    if (sources.length === 1) parts.push(`All from ${sources[0].name.toLowerCase()}`);
    else if (sources.length > 1) parts.push(`Most from ${sources[0].name.toLowerCase()}`);
    if (contacts?.responseRate !== null && contacts?.responseRate !== undefined) parts.push(`${Math.round(contacts.responseRate)}% responded to`);
    return parts.join(' · ');
  }, [contactPie, contacts]);

  return (
    <div className="msg-overview__grid">
      <Card
        title="Message direction"
        subtitle="How your conversations flow"
        csv={{ columns: [{ key: 'name', header: 'Direction' }, { key: 'value', header: 'Messages' }], rows: directionPie, filename: csvFilename('Messages by Direction') }}
      >
        <TotalDonut data={directionPie} total={totals.total} unit="Messages" colorFor={(name) => REPORT_DIRECTION_COLORS[name] || OTHER_COLOR} />
      </Card>
      <Card
        title="Messages by channel"
        subtitle="Share of message volume"
        csv={{ columns: [{ key: 'name', header: 'Channel' }, { key: 'value', header: 'Messages' }], rows: channelPie, filename: csvFilename('Messages by Channel') }}
      >
        <BarList rows={channelRows} />
      </Card>
      <StatCard
        title="Channel type"
        subtitle={topType ? shareSentence(topType.name, topType.share) : 'No messages in this range'}
        value={topType ? percentText(topType.share, 1) : '-'}
        pill={topType?.name}
      />
      <StatCard
        title="New inbound contacts"
        subtitle={contactSummary || 'No new contacts in this range'}
        value={formatCount(contacts?.total || 0)}
        tooltip={['By source', ...[...contactPie].sort((a, b) => b.value - a.value).map((c) => `${c.name}: ${formatCount(c.value)} (${percentText(c.value, contacts?.total || 0)})`)]}
      />
      <StatCard
        title="Average response time"
        subtitle="Time to reply to an inbound message"
        value={avgResponseMinutes === null || avgResponseMinutes === undefined ? '-' : formatDuration(avgResponseMinutes)}
      />
      <StatCard
        title="Average messages per day"
        subtitle={`${formatCount(totals.total)} messages over ${formatCount(dayCount)} ${dayCount === 1 ? 'day' : 'days'}`}
        value={avgPerDay < 10 ? avgPerDay.toFixed(1) : formatCount(Math.round(avgPerDay))}
      />
      <Card
        title="Messages by hour"
        subtitle={`Daily average${peak ? ` · inbound peak ${peak.label}` : ''}`}
        csv={{ columns: [{ key: 'hour', header: 'Hour' }, ...DIRECTION_BARS.map((b) => ({ key: b.key, header: b.name }))], rows: hourSeries, filename: csvFilename('Average Messages Per Hour') }}
        className="msg-overview__card--wide"
        footer="Average messages / hour"
      >
        <StackedBars data={hourSeries} xKey="hour" bars={DIRECTION_BARS} stackId="overview-hr" interval={2} height={260} allowDecimals />
      </Card>

      <h3 className="msg-overview__grid-heading">Trends</h3>
      <Card
        title="Response time by hour"
        subtitle="Average time to reply, by hour the message came in · darker bars had more replies"
        csv={{ columns: [{ key: 'hour', header: 'Hour' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }, { key: 'replies', header: 'Replies' }], rows: hourSeries, filename: csvFilename('Average Response Time by Hour') }}
        className="msg-overview__card--wide"
      >
        {hourSeries.some((h) => h.avgResponseMinutes !== null) ? <ResponseByHourChart data={hourSeries} height={260} /> : <EmptyChart height={260} />}
      </Card>
      <Card
        title="Messages by day of week"
        subtitle="Daily average for each weekday"
        csv={{ columns: [{ key: 'day', header: 'Day' }, ...DIRECTION_BARS.map((b) => ({ key: b.key, header: b.name }))], rows: weekdaySeries, filename: csvFilename('Average Messages by Day of Week') }}
        className="msg-overview__card--wide"
      >
        <StackedBars data={weekdaySeries} xKey="day" bars={DIRECTION_BARS} stackId="overview-dow" interval={0} height={260} allowDecimals />
      </Card>
      <Card
        title="Messages per day"
        subtitle="Inbound, outbound and automated each day"
        csv={{ columns: [{ key: 'day', header: 'Date' }, ...DIRECTION_BARS.map((b) => ({ key: b.key, header: b.name }))], rows: daySeries, filename: csvFilename('Messages Per Day') }}
        className="msg-overview__card--wide"
      >
        <StackedBars data={daySeries} xKey="date" bars={DIRECTION_BARS} stackId="overview-day" height={260} />
      </Card>
      <Card
        title="Response time per day"
        subtitle={avgResponseMinutes === null || avgResponseMinutes === undefined ? 'No replies in this range' : `Range average ${formatDuration(avgResponseMinutes)}`}
        csv={{ columns: [{ key: 'day', header: 'Date' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }], rows: daySeries, filename: csvFilename('Response Time Per Day') }}
        className="msg-overview__card--wide"
      >
        {daySeries.some((d) => d.avgResponseMinutes !== null) ? (
          <ResponsePerDayBars data={daySeries} average={avgResponseMinutes} />
        ) : <EmptyChart height={260} />}
      </Card>
    </div>
  );
};
OverviewSummary.propTypes = {
  totals: PropTypes.shape({ total: PropTypes.number }).isRequired,
  dayCount: PropTypes.number.isRequired,
  directionPie: PropTypes.array.isRequired,
  channelPie: PropTypes.array.isRequired,
  channelTypePie: PropTypes.array.isRequired,
  contacts: PropTypes.object,
  contactPie: PropTypes.array.isRequired,
  hourSeries: PropTypes.array.isRequired,
  weekdaySeries: PropTypes.array.isRequired,
  daySeries: PropTypes.array.isRequired,
  peak: PropTypes.shape({ label: PropTypes.string }),
  avgResponseMinutes: PropTypes.number,
  csvFilename: PropTypes.func.isRequired,
};

export const OverviewMembers = ({
  memberTotals, memberResponse, memberDaily, memberName, memberOpenRows, dayCount, csvFilename,
}) => {
  const openRows = useMemo(() => memberOpenRows.map((m) => ({ key: m.key, name: m.name, value: m.openNow })).sort((a, b) => b.value - a.value), [memberOpenRows]);
  const openTotal = openRows.reduce((sum, r) => sum + r.value, 0);
  const distributionRows = useMemo(
    () => shareRows(memberTotals.map((m) => ({
      key: m.key, name: m.name, value: m.total, extra: [`${formatCount(m.avgPerDay)} per day on average`],
    })), () => REPORT_DIRECTION_COLORS.Inbound, 'messages sent'),
    [memberTotals],
  );
  const responseRows = useMemo(() => responseRowsOf(memberResponse), [memberResponse]);
  return (
    <div className="msg-overview__grid">
      <Card
        title="Open conversations"
        subtitle="Current assignments · independent of date range"
        badge={`${formatCount(openTotal)} open now`}
        csv={{ columns: [{ key: 'name', header: 'Member' }, { key: 'openNow', header: 'Open Now' }], rows: memberOpenRows, filename: csvFilename('Open Conversations Per Member') }}
        className="msg-overview__card--wide"
      >
        <CountTiles rows={openRows} unit="open" empty="No conversations are assigned to members right now." />
      </Card>
      <Card
        title="Message distribution"
        subtitle="Share of messages sent"
        csv={{ columns: [{ key: 'name', header: 'Member' }, { key: 'total', header: 'Messages Sent' }], rows: memberTotals, filename: csvFilename('Message Distribution') }}
        footer="Percentages rounded · inbound messages excluded"
      >
        <BarList rows={distributionRows} withAvatars empty="No messages sent by members in this range." />
      </Card>
      <Card
        title="Average response time"
        subtitle="Slowest first"
        csv={{ columns: [{ key: 'name', header: 'Member' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }], rows: memberResponse, filename: csvFilename('Average Response Time by Member') }}
      >
        <BarList rows={responseRows} withAvatars empty="No replies from members in this range." />
      </Card>
      <OverTimeCard title="Messages over time" series={memberDaily} nameOf={memberName} dayCount={dayCount} csvFilename={csvFilename} />
    </div>
  );
};
OverviewMembers.propTypes = {
  memberTotals: PropTypes.array.isRequired,
  memberResponse: PropTypes.array.isRequired,
  memberDaily: PropTypes.shape({ data: PropTypes.array, keys: PropTypes.array }).isRequired,
  memberName: PropTypes.func.isRequired,
  memberOpenRows: PropTypes.array.isRequired,
  dayCount: PropTypes.number.isRequired,
  csvFilename: PropTypes.func.isRequired,
};

// --- Direction by channel: stacked bars that scroll sideways when channels don't fit ---

const CHANNEL_COLUMN_PX = 128; // room per channel, so names stay readable however many there are
const CHANNEL_CHART_HEIGHT = 300;
const CHANNEL_AXIS_PX = 56;
const CHANNEL_TICK_HEIGHT = 52;

// 0..max split into ~4 round steps (1, 2, 2.5, 5 x 10^n), shared by the pinned axis and the bars.
export function niceTicks(max) {
  if (!max || max <= 0) return [0, 1];
  const raw = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= raw);
  const ticks = [];
  for (let v = 0; v < max + step; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

const truncate = (text, length) => (text.length > length ? `${text.slice(0, length - 1)}…` : text);

// Two-line x tick: channel name, then its (visible) message count and any status tag.
const ChannelTick = ({ x, y, payload, rows }) => {
  const row = rows.find((r) => r.key === payload.value);
  if (!row) return null;
  const { label, status } = splitStatus(row.name);
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{row.name}</title>
      <text textAnchor="middle" dy={16} fontSize={13} fill="#1f2a37">{truncate(label, 18)}</text>
      <text textAnchor="middle" dy={34} fontSize={12} fill="#6b7684">
        {`${formatCount(row.visibleTotal)} messages${status ? ` · ${status}` : ''}`}
      </text>
    </g>
  );
};
ChannelTick.propTypes = {
  x: PropTypes.number, y: PropTypes.number, payload: PropTypes.shape({ value: PropTypes.any }), rows: PropTypes.array.isRequired,
};

const ChannelTooltip = ({ active, payload, rows }) => {
  if (!active || !payload || !payload.length) return null;
  const row = rows.find((r) => r.key === payload[0].payload.key);
  if (!row) return null;
  return (
    <div className="msg-overview__hover-tip msg-overview__hover-tip--inline">
      <div className="msg-overview__hover-tip-title">{row.name}</div>
      {DIRECTION_BARS.map((b) => <div key={b.key}>{`${b.name}: ${formatCount(row[b.key] || 0)} (${percentText(row[b.key] || 0, row.total)})`}</div>)}
      <div>{`Total: ${formatCount(row.total)}`}</div>
    </div>
  );
};
ChannelTooltip.propTypes = { active: PropTypes.bool, payload: PropTypes.array, rows: PropTypes.array.isRequired };

const DirectionByChannelChart = ({ channels }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const [width, setWidth] = useState(0);
  const scrollRef = useRef(null);
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;
    setWidth(node.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visibleBars = DIRECTION_BARS.filter((b) => !hidden.has(b.key));
  // Sorted by what is shown, so hiding a direction re-ranks the channels by the rest.
  const rows = useMemo(() => channels.map((c) => ({
    ...c,
    visibleTotal: DIRECTION_BARS.filter((b) => !hidden.has(b.key)).reduce((sum, b) => sum + (c[b.key] || 0), 0),
  })).sort((a, b) => b.visibleTotal - a.visibleTotal || b.total - a.total), [channels, hidden]);
  const ticks = niceTicks(Math.max(0, ...rows.map((r) => r.visibleTotal)));
  const domain = [0, ticks[ticks.length - 1]];
  const chartWidth = Math.max(width, rows.length * CHANNEL_COLUMN_PX);
  const overflows = width > 0 && rows.length * CHANNEL_COLUMN_PX > width;
  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  if (!rows.length) return <p className="msg-overview__empty">No messages on any channel in this range.</p>;
  const margin = { top: 8, right: 0, bottom: 0, left: 0 };
  return (
    <div>
      <div className="msg-overview__chart-legend" role="group" aria-label="Show or hide message directions">
        {DIRECTION_BARS.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`msg-overview__legend-btn${hidden.has(b.key) ? ' msg-overview__legend-btn--off' : ''}`}
            aria-pressed={!hidden.has(b.key)}
            onClick={() => toggle(b.key)}
          >
            <span className="msg-overview__dot" style={{ background: b.color }} />
            {b.name}
          </button>
        ))}
      </div>
      <div className="msg-overview__chart-meta">
        <span>{`${formatCount(rows.length)} ${rows.length === 1 ? 'channel' : 'channels'}`}</span>
        <span>Sorted by total volume ↓</span>
      </div>
      <div className="msg-overview__scroll-chart">
        <div className="msg-overview__scroll-chart-axis" style={{ width: CHANNEL_AXIS_PX }}>
          <BarChart width={CHANNEL_AXIS_PX} height={CHANNEL_CHART_HEIGHT} data={rows} margin={margin}>
            <XAxis dataKey="key" height={CHANNEL_TICK_HEIGHT} tick={false} axisLine={false} tickLine={false} />
            <YAxis domain={domain} ticks={ticks} width={CHANNEL_AXIS_PX} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatCount} />
          </BarChart>
        </div>
        <div className="msg-overview__scroll-chart-body" ref={scrollRef}>
          {width > 0 && (
            <BarChart width={chartWidth} height={CHANNEL_CHART_HEIGHT} data={rows} margin={margin} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="0" stroke="#eef1f5" vertical={false} />
              <XAxis dataKey="key" height={CHANNEL_TICK_HEIGHT} interval={0} tickLine={false} tick={<ChannelTick rows={rows} />} />
              <YAxis domain={domain} ticks={ticks} hide />
              <Tooltip content={<ChannelTooltip rows={rows} />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} wrapperStyle={{ zIndex: 10 }} />
              {visibleBars.map((b, i) => (
                <Bar key={b.key} dataKey={b.key} name={b.name} stackId="direction" fill={b.color} maxBarSize={64} isAnimationActive={false} radius={i === visibleBars.length - 1 ? [4, 4, 0, 0] : undefined} />
              ))}
            </BarChart>
          )}
        </div>
      </div>
      {overflows && <p className="msg-overview__chart-hint">{`Scroll sideways to see all ${formatCount(rows.length)} channels →`}</p>}
    </div>
  );
};
DirectionByChannelChart.propTypes = { channels: PropTypes.array.isRequired };

export const OverviewChannels = ({
  channelTotals, channelResponse, channelDaily, channelName, dayCount, csvFilename,
}) => {
  const active = useMemo(() => channelTotals.filter((c) => c.total > 0), [channelTotals]);
  const shareList = useMemo(() => shareRows(active.map((c) => ({
    key: c.key, name: c.name, value: c.total, extra: [`${formatCount(c.avgPerDay)} per day on average`],
  })), (i) => categoryColor(i), 'messages'), [active]);
  const responseRows = useMemo(() => responseRowsOf(channelResponse), [channelResponse]);
  return (
    <div className="msg-overview__grid">
      <Card
        title="Messages by channel"
        subtitle="Share of message volume"
        csv={{
          columns: [{ key: 'name', header: 'Channel' }, { key: 'total', header: 'Messages' }, { key: 'avgPerDay', header: 'Avg. Messages/Day' }],
          rows: channelTotals,
          filename: csvFilename('Messages by Channel'),
        }}
      >
        <BarList rows={shareList} empty="No messages on any channel in this range." />
      </Card>
      <Card
        title="Average response time"
        subtitle="Slowest first"
        csv={{ columns: [{ key: 'name', header: 'Channel' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }], rows: channelResponse, filename: csvFilename('Average Response Time by Channel') }}
      >
        <BarList rows={responseRows} empty="No replies on any channel in this range." />
      </Card>
      <Card
        title="Direction by channel"
        subtitle="Inbound, outbound and automated messages"
        csv={{ columns: [{ key: 'name', header: 'Channel' }, ...DIRECTION_BARS.map((b) => ({ key: b.key, header: b.name })), { key: 'total', header: 'Total' }], rows: channelTotals, filename: csvFilename('Direction by Channel') }}
        className="msg-overview__card--wide"
      >
        <DirectionByChannelChart channels={active} />
      </Card>
      <OverTimeCard title="Messages over time" series={channelDaily} nameOf={channelName} dayCount={dayCount} csvFilename={csvFilename} />
    </div>
  );
};
OverviewChannels.propTypes = {
  channelTotals: PropTypes.array.isRequired,
  channelResponse: PropTypes.array.isRequired,
  channelDaily: PropTypes.shape({ data: PropTypes.array, keys: PropTypes.array }).isRequired,
  channelName: PropTypes.func.isRequired,
  dayCount: PropTypes.number.isRequired,
  csvFilename: PropTypes.func.isRequired,
};

export const OverviewGroups = ({ groupData, csvFilename }) => {
  const { rows, nameOf, closedPerDay, closedKeys, totalOpen, totalClosed } = groupData;
  const openRows = useMemo(() => rows.filter((r) => r.open > 0).map((r) => ({ key: r.key, name: r.name, value: r.open })).sort((a, b) => b.value - a.value), [rows]);
  const assignedRows = useMemo(() => countRows(rows.map((r) => ({ key: r.key, name: r.name, value: r.routed })), REPORT_DIRECTION_COLORS.Inbound, 'assignments'), [rows]);
  const closedRows = useMemo(() => countRows(rows.map((r) => ({ key: r.key, name: r.name, value: r.closed })), RESPONSE_COLOR, 'closed'), [rows]);
  const closedBars = closedKeys.map((k, i) => ({
    key: k,
    name: nameOf(k === OTHER_KEY ? OTHER_KEY : Number(k)),
    color: k === OTHER_KEY ? OTHER_COLOR : categoryColor(i),
  }));
  return (
    <div className="msg-overview__grid">
      <Card
        title="Open conversations"
        subtitle="Currently assigned to each group · independent of date range"
        badge={`${formatCount(totalOpen)} open now`}
        csv={{ columns: [{ key: 'name', header: 'Group' }, { key: 'open', header: 'Open Now' }], rows, filename: csvFilename('Open Conversations Per Group') }}
        className="msg-overview__card--wide"
      >
        <CountTiles rows={openRows} unit="open" empty="No conversations are assigned to groups right now." />
      </Card>
      <Card
        title="Conversations assigned"
        subtitle="Times a conversation was assigned to the group"
        csv={{ columns: [{ key: 'name', header: 'Group' }, { key: 'routed', header: 'Conversations Assigned' }], rows, filename: csvFilename('Conversations Assigned to Group') }}
      >
        <BarList rows={assignedRows} empty="No conversations were assigned to groups in this range." />
      </Card>
      <Card
        title="Conversations closed"
        subtitle={`${formatCount(totalClosed)} closed in this range`}
        csv={{ columns: [{ key: 'name', header: 'Group' }, { key: 'closed', header: 'Conversations Closed' }], rows, filename: csvFilename('Conversations Closed by Group') }}
      >
        <BarList rows={closedRows} empty="No conversations were closed in this range." />
      </Card>
      <Card
        title="Closed per day"
        subtitle="By group"
        csv={{ columns: [{ key: 'day', header: 'Date' }, ...closedBars.map((b) => ({ key: b.key, header: b.name }))], rows: closedPerDay, filename: csvFilename('Closed Conversations Per Day by Group') }}
        className="msg-overview__card--wide"
      >
        <StackedBars data={closedPerDay} xKey="date" bars={closedBars} stackId="overview-closed" height={260} />
      </Card>
    </div>
  );
};
OverviewGroups.propTypes = {
  groupData: PropTypes.shape({
    rows: PropTypes.array, nameOf: PropTypes.func, closedPerDay: PropTypes.array, closedKeys: PropTypes.array, totalOpen: PropTypes.number, totalClosed: PropTypes.number,
  }).isRequired,
  csvFilename: PropTypes.func.isRequired,
};

export const OverviewReminders = ({ reminders, colors, csvFilename }) => {
  const colorFor = (name, i) => colors[name] || categoryColor(i);
  const count = (label) => reminders.byResponse.find((r) => r.name === label)?.value || 0;
  const csv = {
    columns: [{ key: 'response', header: 'Response' }, { key: 'count', header: 'Reminders' }, { key: 'pct', header: '% of Sent' }],
    rows: reminders.rows,
    filename: csvFilename('Reminder Responses'),
  };
  return (
    <div className="msg-overview__grid">
      <Card title="Reminder responses" subtitle="How patients answered" csv={csv}>
        <TotalDonut data={reminders.byResponse} total={reminders.total} unit="Reminders" colorFor={colorFor} />
      </Card>
      <div className="msg-overview__stack">
        <StatCard title="Responded" subtitle="Confirmed or cancelled" value={`${reminders.rate(reminders.responded)}%`} />
        <StatCard title="Confirmed" subtitle={`${formatCount(count('Confirmed'))} of ${formatCount(reminders.total)} reminders`} value={`${reminders.rate(count('Confirmed'))}%`} />
      </div>
      <Card
        title="Reminders per day"
        subtitle="By response"
        csv={{ columns: [{ key: 'date', header: 'Date' }, ...reminders.labels.map((l) => ({ key: l, header: l }))], rows: reminders.perDay, filename: csvFilename('Reminders Per Day') }}
        className="msg-overview__card--wide"
      >
        <StackedBars data={reminders.perDay} xKey="date" bars={reminders.labels.map((l, i) => ({ key: l, name: l, color: colorFor(l, i) }))} stackId="overview-rem" height={260} />
      </Card>
    </div>
  );
};
OverviewReminders.propTypes = {
  reminders: PropTypes.shape({
    total: PropTypes.number,
    byResponse: PropTypes.array,
    perDay: PropTypes.array,
    labels: PropTypes.array,
    rows: PropTypes.array,
    responded: PropTypes.number,
    rate: PropTypes.func,
  }).isRequired,
  colors: PropTypes.object.isRequired,
  csvFilename: PropTypes.func.isRequired,
};
